import { Filter as FilterBackboneModel } from 'aqTypes';
import type {
  CreateComponentLabelFilter,
  CreateFilterOptions,
  CreateLabelFormatting,
  CreateLabelOptions,
  CreateReferenceLabelFilter,
  FilterInterfaceImplementation,
  FiltersAsQueryBuilderQueries,
  GetAllReferenceLabelFilters,
  GetComponentLabelFormattingFields,
  GetConditionalFormattingFilters,
  GetFilterCount,
  GetFormattingAndLabelRules,
  GetFormattingFilters,
  GetLabelFormatting,
  GetNonFormattingFilterCount,
  GetReferenceLabelFilter,
  PerspectivesFilters,
  RemoveComponentLabelFormattingFilters,
  RemoveReferenceLabelFormattingFilters,
  UpdateFiltered,
} from '@ardoq/filter-interface';
import { GetComponentLabelFilter } from '@ardoq/filter-interface';
import {
  findQuickPerspectivesTypeEqualitySubquery,
  getLabelFormattingRules,
} from '@ardoq/filter-helpers';
import { ComponentLabelSource } from '@ardoq/data-model';
import {
  APIEntityType,
  type ArdoqId,
  type FilterInterfaceFilter,
  type FormattingFilter,
  type FormattingOperator,
  FilterTypes,
  type QueryBuilderSubquery,
  FilterAttributes,
  isFormatting,
} from '@ardoq/api-types';
import {
  getComponentLabelFormattingFilterAttributes,
  getReferenceLabelFormattingFilterAttributes,
} from './formattingRulesToFormattingAttributes';
import { labelFormattingToFilterAttributes } from './util';
import { componentInterface } from '@ardoq/component-interface';
import { globalFilters as Filters } from 'collections/filters';
import { fieldInterface } from '@ardoq/field-interface';
import { backboneFilterToFilterAttributes } from 'collections/toPureDataFilterUtils';
import { getCurrentGraphModel } from 'modelInterface/graphModel$';
import {
  DEFAULT_LEGACY_REFERENCE_LABEL_FORMATTING_FILTER_ATTRIBUTES,
  DEFAULT_MULTI_LABEL_REFERENCE_FORMATTING_FILTER_ATTRIBUTES,
} from '@ardoq/graph';

type ToModel = (filter: FilterBackboneModel) => FilterInterfaceFilter;
const toModel: ToModel = filter => ({
  id: 'cid' in filter ? filter.cid : undefined,
  affectComponent: false,
  affectReference: false,
  comparator: undefined,
  isNegative: false,
  name: '',
  type: '',
  value: '',
  ...filter.attributes,
});

const getAllReferenceLabelFilters: GetAllReferenceLabelFilters = () => {
  const allFilters = Filters.getFiltersByType(FilterTypes.REFERENCE_LABEL).map(
    toModel
  );

  return allFilters.length > 0
    ? allFilters
    : DEFAULT_MULTI_LABEL_REFERENCE_FORMATTING_FILTER_ATTRIBUTES;
};
const getReferenceLabelFilter: GetReferenceLabelFilter = () => {
  const [filter] = Filters.getRefLabelFilter();
  return filter
    ? toModel(filter)
    : DEFAULT_LEGACY_REFERENCE_LABEL_FORMATTING_FILTER_ATTRIBUTES;
};

/**
 * Returns the reference types of the quick perspective filters only if there
 * are no advanced filter rules.
 * In a mix of advanced and quick perspective filters the meaning would be
 * ambiguous. */
const getActiveQuickReferenceTypeFilters = () => {
  const referenceFilters =
    Filters.getFiltersAsQueryBuilderQueries()?.referenceRules;
  if (!referenceFilters) {
    return null;
  }

  const referenceTypesSubquery =
    findQuickPerspectivesTypeEqualitySubquery(referenceFilters);

  if (!referenceTypesSubquery) {
    return null;
  }

  const selectedReferenceTypes = referenceTypesSubquery.rules.map(
    ({ value }) => value
  );
  return selectedReferenceTypes.length > 0
    ? new Set(selectedReferenceTypes)
    : null;
};

const getComponentLabelFilter: GetComponentLabelFilter = () => {
  const [filter] = Filters.getCompLabelFilter();
  return filter
    ? toModel(filter)
    : {
        affectComponent: true,
        affectReference: false,
        isNegative: false,
        name: '',
        type: FilterTypes.COMPONENT_LABEL,
        value: ComponentLabelSource.NAME,
      };
};

const getComponentLabelFormattingFields: GetComponentLabelFormattingFields =
  () => new Set<string>(Filters.getCompLabelFilter().map(f => f.get('value')));

const getLabelFormatting: GetLabelFormatting = () =>
  getLabelFormattingRules(Filters.map(backboneFilterToFilterAttributes));

/** Returns the count of active filters. Note: this does not include formatting filters. */
const getNonFormattingFilterCount: GetNonFormattingFilterCount = () =>
  Filters.getFilterCount();

// feel free to modify these param types. I am building them ad-hoc based on legacy usage, as I convert more code to use filterInterface.
const createFilter: (
  attributes: Omit<FilterInterfaceFilter, 'type' | 'comparator'> & {
    filterName?: string;
    comparator?: FormattingOperator;
  },
  options: CreateFilterOptions
) => void = (attributes, options) => {
  Filters.createFilter(attributes, options);
};

const loadFilters = (filters: FilterAttributes[]) => {
  resetFilters();
  filters.forEach(filter => {
    // shouldTriggerChangeEvent is backbone-stuff, we're trying to move away from it so we're setting it to false
    // use dedicated action triggerFiltersChangedEvent to notify the app about the change instead
    Filters.createFilter(filter, { shouldTriggerChangeEvent: false });
  });
};

const createReferenceLabelFilter: CreateReferenceLabelFilter = (
  referenceLabelFormattingValue,
  { includeTime, transactionId }: CreateLabelOptions = {}
) => {
  const filterAttributes = getReferenceLabelFormattingFilterAttributes(
    referenceLabelFormattingValue,
    includeTime
  );

  Filters.createFilter(filterAttributes, {
    shouldTriggerChangeEvent: true,
    transactionId,
  });
};

const createComponentLabelFilter: CreateComponentLabelFilter = (
  componentLabelFormattingValue,
  { includeTime, transactionId }: CreateLabelOptions = {}
) => {
  const filterAttributes = getComponentLabelFormattingFilterAttributes(
    componentLabelFormattingValue,
    includeTime
  );

  Filters.createFilter(filterAttributes, {
    shouldTriggerChangeEvent: true,
    transactionId,
  });
};
const createLabelFormatting: CreateLabelFormatting = (
  formatting,
  transactionId
) => {
  const filterAttributes = formatting.flatMap(
    labelFormattingToFilterAttributes
  );
  for (const currentFilterAttributes of filterAttributes) {
    Filters.createFilter(currentFilterAttributes, {
      shouldTriggerChangeEvent: true,
      transactionId,
    });
  }
};

const removeReferenceLabelFormattingFilters: RemoveReferenceLabelFormattingFilters =
  shouldTriggerChangeEvent => {
    Filters.removeReferenceLabelFormattingFilters({ shouldTriggerChangeEvent });
  };

const removeComponentLabelFormattingFilters: RemoveComponentLabelFormattingFilters =
  shouldTriggerChangeEvent => {
    Filters.removeComponentLabelFormattingFilters({ shouldTriggerChangeEvent });
  };

const setConditionalFormattingFilters = (
  formattingRules: FilterAttributes[],
  shouldGreyOut: boolean,
  transactionId?: string
) => {
  Filters.setConditionalFormattingFilters({
    formattingRules,
    shouldGreyOut,
    transactionId,
  });
};

/**
 * Updates the filter cache for a component by checking if its filter state has changed.
 * This is a workaround to a technical deficiency caused by the cache itself.
 * @param componentId - The ID of the component to update
 * @param scopeSource - The scope to resolve field-sorted values against when recomputing conditional formatting
 */
const updateFiltered: UpdateFiltered = (componentId, scopeSource) => {
  const attributes = componentInterface.get(componentId);
  if (!attributes) {
    return;
  }
  Filters.isComponentIncludedInContextByFilter(
    scopeSource,
    {
      attributes,
      fields: fieldInterface.getFieldsByComponentType(
        attributes.typeId,
        attributes.model
      ),
      typeName: componentInterface.getTypeName(componentId) ?? '',
      incomingRefCount: componentInterface.getSources(componentId).length,
      outgoingRefCount: componentInterface.getTargets(componentId).length,
    },
    getCurrentGraphModel(),
    true
  );
};

const getFormattingFilters: GetFormattingFilters = () =>
  Filters.getFormattingFilters().map(toModel) as FormattingFilter[];

const getConditionalFormattingFilters: GetConditionalFormattingFilters = () =>
  Filters.getConditionalFormattingFilters().map(({ attributes }) =>
    structuredClone(attributes)
  );

const getFiltersAsQueryBuilderQueries = (): FiltersAsQueryBuilderQueries =>
  Filters.getFiltersAsQueryBuilderQueries();

const getColorFiltersForComponent = (modelId: ArdoqId): string[] => {
  const attrs = componentInterface.get(modelId);
  if (!attrs) return [];
  const fields = fieldInterface.getFieldsByComponentType(
    attrs.typeId,
    attrs.model
  );
  const referenceAdjacency = getCurrentGraphModel();
  return Filters.getFormattingFilters()
    .filter(
      filter =>
        filter.get('color') &&
        filter.isAffected(APIEntityType.COMPONENT) &&
        filter.isIncluded(
          APIEntityType.COMPONENT,
          attrs,
          fields,
          referenceAdjacency,
          null
        )
    )
    .map(({ cid }) => cid);
};

const containsAnyNestedRule = (subquery: QueryBuilderSubquery) =>
  subquery.rules.length !== 0;

/**
 * Query builder returns a subquery when there are no rules defined
 * { condition: "OR"; rules: [] }
 *
 * Arqod-front should ignore such rules.
 */
const applyPerspectiveFilters = (filters: PerspectivesFilters) => {
  const componentRules = containsAnyNestedRule(filters.componentFilterRules)
    ? filters.componentFilterRules
    : null;
  const referenceRules = containsAnyNestedRule(filters.referenceFilterRules)
    ? filters.referenceFilterRules
    : null;

  Filters.setFiltersFromQueryBuilderQueries({
    componentRules,
    referenceRules,
  });
  Filters.updateWorkspaceFilter(filters.filterByWorkspaceIds);

  // Old perspective editor would keep selection in the Perspective editor and the main app
  // automatically, without using 'Apply' button. That means that we don't handle
  // `toggleDynamicFilterActive` and `selectDynamicFilterOption` actions in Perspective editor reducers,
  // but pass them to the main app and expect to see the updated through `notifyFiltersChange` action
  // with activated/deactivated graph filters as part of the payload.
  // As a consequence, we don't apply Graph filters manually when user clicks 'Apply' or 'Save' button
  // in the perspective editor.
};

const resetFilters = () => Filters.reset();

const getFilterCount: GetFilterCount = () => {
  return Filters.length;
};

// Backbone attributes are now two levels deep frozen, so no need to clone
// them when we map them to the filter attributes.
const getFormattingAndLabelRules: GetFormattingAndLabelRules = () => ({
  rules: Filters.map(toModel).filter(isFormatting),
  transactionId: Filters.transactionId,
});

/**
 * `filterInterface` is a Backbone-era singleton, and the one whose source of
 * truth has not moved yet: the filters still live in the Backbone collection
 * `globalFilters` (`collections/filters.ts`), so there is no canonical `filters$`
 * state stream to point at.
 *
 * What exists instead is a set of derived streams under `streams/filters/` --
 * `formattingAndLabelRules$`, `conditionalFormattingFilters$`,
 * `referenceLabelFilters$`, `referenceLabelFilterField$`,
 * `activeComponentColors$`, `quickPerspectiveReferenceTypeFilters$`,
 * `includedInContext$`, `activeFilter$`, and `componentFilterAttributes$` /
 * `referenceFilterAttributes$`. Those streams are the boundary that owns the
 * imperative read; join them and thread the resolved rules downstream as plain
 * parameters instead of calling this interface from leaf code. Pure logic belongs
 * in `filterOperations` (`@ardoq/core`), `filterColorDataOperations`
 * (`@ardoq/scope-data`) or the pure helper modules in `@ardoq/filter-helpers`.
 *
 * Mutating members dispatch through `streams/filters/FilterActions.ts`; prefer
 * dispatching the action over calling the mutator.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const filterInterface: FilterInterfaceImplementation = {
  /**
   * @deprecated Join `referenceLabelFilters$` (`streams/filters/referenceLabelFilters$.ts`) and narrow with `filterOperations.isReferenceLabelFormattingFilter` (`@ardoq/core`).
   */
  getAllReferenceLabelFilters,
  /**
   * @deprecated Join `referenceLabelFilterField$` (`streams/filters/referenceLabelFilterField$.ts`).
   */
  getReferenceLabelFilter,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and read the label formatting off the resolved bundle.
   */
  getLabelFormatting,
  /**
   * @deprecated Join `quickPerspectiveReferenceTypeFilters$` (`streams/filters/quickPerspectiveReferenceTypeFilters$.ts`).
   */
  getActiveQuickReferenceTypeFilters,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and read the component label rule off the resolved bundle.
   */
  getComponentLabelFilter,
  /**
   * @deprecated Join `formattingAndLabelRules$` and `fields$`, then select with `fieldOperations` (`@ardoq/core`).
   */
  getComponentLabelFormattingFields,
  /**
   * @deprecated Join a derived stream under `streams/filters/` and count with `filterOperations.isStrictlyFormattingFilter` (`@ardoq/core`) rather than reading the Backbone collection.
   */
  getNonFormattingFilterCount,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createFilter,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createReferenceLabelFilter,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createComponentLabelFilter,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createLabelFormatting,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  removeReferenceLabelFormattingFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  removeComponentLabelFormattingFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  setConditionalFormattingFilters,
  /**
   * @deprecated Dispatch `updateFiltered` from `streams/filters/FilterActions.ts` instead of calling through the interface.
   */
  updateFiltered,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and narrow with `filterOperations.isStrictlyFormattingFilter` (`@ardoq/core`).
   */
  getFormattingFilters,
  /**
   * @deprecated Join `conditionalFormattingFilters$` (`streams/filters/conditionalFormattingFilters$.ts`) and narrow with `filterOperations.isComponentFormatting` / `isReferenceFormatting` (`@ardoq/core`).
   */
  getConditionalFormattingFilters,
  /**
   * @deprecated Join `activeFilter$` (`streams/filters/activeFilter$.ts`) and read `activeFilters`.
   */
  getFiltersAsQueryBuilderQueries,
  /**
   * @deprecated Join `activeComponentColors$` (`streams/filters/activeComponentColors$.ts`), or call `filterColorDataOperations.getComponentFilterColor` (`@ardoq/scope-data`).
   */
  getColorFiltersForComponent,
  /**
   * @deprecated Join a derived stream under `streams/filters/` and count the resolved filters, rather than reading the Backbone collection at call time.
   */
  getFilterCount,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  loadFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  applyPerspectiveFilters,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) -- it exposes exactly this bundle.
   */
  getFormattingAndLabelRules,
};
